// L4 — turn the checklist into a lead-magnet by giving the user a clear
// "Save this checklist" CTA. For anonymous users this captures an email →
// triggers Stage 5's magic-link soft signup (source: checklist_save), so
// they can come back to the same checklist from any device just by clicking
// the link in their inbox.
//
// Auth users see a confirmation instead of a form. Real D1 sync of the
// progress object is deferred to a follow-up — the value today is the
// soft signup, not the progress sync, so we ship the lead capture first.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/lib/auth-client';
import { submitNotify } from '@/lib/notify';

const STORAGE_KEY_PREFIX = 'rm:checklist-saved:';

function localKey(country: string): string {
  return `${STORAGE_KEY_PREFIX}${country.toUpperCase()}`;
}

function isSavedLocally(country: string): boolean {
  try {
    return localStorage.getItem(localKey(country)) === '1';
  } catch {
    return false;
  }
}

function markSavedLocally(country: string): void {
  try {
    localStorage.setItem(localKey(country), '1');
  } catch {
    // Storage blocked (private mode etc.) — non-fatal, button will re-appear
    // next visit; no business impact since the email already left.
  }
}

interface Props {
  /** ISO-3166 alpha-2 of the checklist's country. */
  country: string;
  /** Human-readable country name for copy. Falls back to the code. */
  countryName?: string;
}

export function SaveChecklistButton({ country, countryName }: Props) {
  const { t } = useTranslation();
  const { data: session, isPending } = useSession();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState<boolean>(() => isSavedLocally(country));
  const [failed, setFailed] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // After the form opens via user click, focus the email input so a sighted
  // user can type immediately. We use useEffect (not the autoFocus prop)
  // because autoFocus is flagged by a11y linters and can confuse screen
  // readers — the user already initiated the action, so moving focus is
  // expected here.
  useEffect(() => {
    if (open) emailInputRef.current?.focus();
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.includes('@') || submitting) return;
    setSubmitting(true);
    setFailed(false);
    const ok = await submitNotify({
      email,
      countryCode: country.toUpperCase(),
      source: 'checklist_save',
      context: { country: country.toUpperCase(), surface: 'next-steps-checklist' },
    });
    setSubmitting(false);
    if (!ok) {
      // Nothing was stored and no link is on its way. Keep the form open with
      // the address still in it so a retry costs one click, and say so — the
      // confirmation below would be a lie.
      setFailed(true);
      return;
    }
    markSavedLocally(country);
    setSaved(true);
    setOpen(false);
  };

  // Logged-in users: confirmation state. D1 sync of the actual progress object
  // will land in a follow-up; the soft signup is the value being captured.
  if (session && !isPending) {
    return (
      <p className="font-mono text-[11px] text-[color:var(--color-moss-600)]">
        ✓ {t('nextSteps.save.authConfirm', {
          defaultValue: 'Saved — progress is tied to your account.',
        })}
      </p>
    );
  }

  if (saved) {
    return (
      <p
        className="font-mono text-[11px] text-[color:var(--color-moss-600)]"
        data-testid={`checklist-saved-${country.toUpperCase()}`}
      >
        ✓ {t('nextSteps.save.anonConfirm', {
          defaultValue: 'Check your inbox — we sent a one-time sign-in link so you can return to this checklist from any device.',
        })}
      </p>
    );
  }

  if (open) {
    return (
      <div className="flex flex-col gap-1.5">
        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-2"
          data-testid={`checklist-save-form-${country.toUpperCase()}`}
        >
          <input
            ref={emailInputRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('nextSteps.save.placeholder', { defaultValue: 'you@example.com' })}
            required
            className="min-w-0 flex-1 rounded border border-[color:var(--color-ink-200)] bg-[color:var(--color-paper)] px-3 py-1.5 font-mono text-xs text-[color:var(--color-ink-900)] placeholder-[color:var(--color-ink-400)] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-moss-600)]"
          />
          <button
            type="submit"
            disabled={submitting || !email.includes('@')}
            className="shrink-0 rounded bg-[color:var(--color-moss-600)] px-3 py-1.5 font-mono text-xs font-medium text-white hover:bg-[color:var(--color-moss-700)] disabled:opacity-50"
          >
            {submitting
              ? t('nextSteps.save.sending', { defaultValue: 'Saving…' })
              : t('nextSteps.save.submit', { defaultValue: 'Save & email me a link' })}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="font-mono text-xs text-[color:var(--color-ink-400)] hover:text-[color:var(--color-ink-700)]"
            aria-label={t('nextSteps.save.cancel', { defaultValue: 'Cancel' })}
          >
            ✕
          </button>
        </form>
        {failed && (
          <p
            className="font-mono text-[11px] text-[color:var(--color-rust-600)]"
            role="status"
            data-testid={`checklist-save-error-${country.toUpperCase()}`}
          >
            {t('nextSteps.save.failed')}
          </p>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-moss-500)] bg-[color:var(--color-paper)] px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-[color:var(--color-moss-700)] transition hover:bg-[color:var(--color-moss-50)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-moss-600)]"
      data-testid={`checklist-save-btn-${country.toUpperCase()}`}
    >
      <span aria-hidden="true">💾</span>
      {t('nextSteps.save.cta', {
        country: countryName ?? country,
        defaultValue: 'Save this checklist',
      })}
    </button>
  );
}
